import Avatar from "../components/ds/Avatar";
import Badge from "../components/ds/Badge";
import Button from "../components/ds/Button";
import { FieldLabel } from "../components/ds/Field";
import { PopUpSelect } from "../components/ds/PopUpButton";
import Icon from "../components/ds/Icon";
import Page from "../components/ds/Page";
import PageHeader from "../components/ds/PageHeader";
import Switch from "../components/ds/Switch";
import TextField from "../components/ds/TextField";
import { useRef, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { FaInstagram, FaFacebookF, FaTiktok, FaYoutube } from "react-icons/fa";
import { FiUsers, FiLoader, FiX } from "react-icons/fi";
import { useOpenSpace } from "../context/SpaceGateContext";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import { getUserProfile, getFollowStats, profileUserFromApiResponse } from "../api/profile";
import InstagramVerificationModal from "../components/InstagramVerificationModal";
import { getAllMyMintSpaces } from "../api/mintSpace";
import { checkCanMessage, sendMessageRequest } from "../api/messages";
import { getBalance } from "../api/balance";
import DeleteAccountSection from "../components/account/DeleteAccountSection";
import FollowButton from "../components/FollowButton";
import FollowListModal from "../components/FollowListModal";
import ProfileHero from "../components/ProfileHero";
import { getProfileAvatarUrl } from "../utils/userDisplay";
import { socialUrl } from "../utils/socialUrl";

const SOCIAL_BADGES = [
  ["youtube", "YouTube"],
  ["instagram", "Instagram"],
  ["facebook", "Facebook"],
  ["tiktok", "TikTok"],
];
const FIELD_PAIRS = "grid grid-cols-1 gap-x-space-16 gap-y-space-12 min-[600px]:grid-cols-2";

const ProfilePage = ({
  isDark,
  bannerImage,
  setBannerImage,
  avatarImage,
  setAvatarImage,
  profileData,
  setProfileData,
  socials,
  setSocials,
  profilePreview,
  setProfilePreview,
  socialModal,
  setSocialModal,
  tempSocialUrl,
  setTempSocialUrl,
  handleSocialChange,
  handleBannerChange,
  handleAvatarChange,
  bannerInputRef,
  avatarInputRef,
  setToast,
  isUploadingBanner,
  isUploadingAvatar
}) => {
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const openSpace = useOpenSpace();
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState(null); // null, "joinedMintSpaces", "createdMintSpaces"
  const [viewingUser, setViewingUser] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [messageStatus, setMessageStatus] = useState(null);
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [balance, setBalance] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [userMintSpaces, setUserMintSpaces] = useState([]);
  const [myMintSpaces, setMyMintSpaces] = useState([]);
  const [joinedMintSpacesPrivateOther, setJoinedMintSpacesPrivateOther] = useState(false);
  const [showInstagramVerificationModal, setShowInstagramVerificationModal] = useState(false);
  const [instagramVerified, setInstagramVerified] = useState(false);
  const [followStats, setFollowStats] = useState(null);
  const [followList, setFollowList] = useState(null); // null | "followers" | "following"
  const profileFieldsRef = useRef(null);
  
  const isViewingOtherUser = userId && userId !== (user?.id ?? user?._id);
  const profileUserId = isViewingOtherUser ? userId : user?.id ?? user?._id;
  const isProfileEditMode = isAuthenticated && !isViewingOtherUser && !profilePreview;

  // Edit mode is global state — always exit when viewing someone else's profile.
  useEffect(() => {
    if (isViewingOtherUser) {
      setProfilePreview(true);
      setActiveSection(null);
    }
  }, [isViewingOtherUser, userId, setProfilePreview]);

  const buildSocialUrl = socialUrl;


  // Fetch user profile if viewing another user
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (isViewingOtherUser && userId) {
        try {
          setLoadingProfile(true);
          const response = await getUserProfile(userId);
          if (response.success) {
            setViewingUser(response.data);
            
            // Check message status
            const msgStatus = await checkCanMessage(userId);
            if (msgStatus.success) {
              setMessageStatus(msgStatus.data);
            }
            
            try {
              const mintSpacesResponse = await getAllMyMintSpaces(userId);
              if (mintSpacesResponse.success) {
                setUserMintSpaces(mintSpacesResponse.data || []);
                setJoinedMintSpacesPrivateOther(!!mintSpacesResponse.joinedMintSpacesPrivate);
              } else {
                setUserMintSpaces([]);
                setJoinedMintSpacesPrivateOther(false);
              }
            } catch (error) {
              setUserMintSpaces([]);
              setJoinedMintSpacesPrivateOther(false);
            }
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
          if (setToast) {
            setToast(t("profile.failedToLoadUserProfile"));
          }
          navigate("/");
        } finally {
          setLoadingProfile(false);
        }
      } else {
        setViewingUser(null);
        setMessageStatus(null);
        setUserMintSpaces([]);
        setJoinedMintSpacesPrivateOther(false);
        
        // Load Instagram verification status for current user
        if (user?.id) {
          try {
            const response = await getUserProfile(user.id);
            const profileUser = profileUserFromApiResponse(response, user.id);
            if (profileUser?.instagramVerification) {
              setInstagramVerified(!!profileUser.instagramVerification.verified);
            }
          } catch (error) {
            console.error("Error loading Instagram verification:", error);
          }
        }
      }
    };
    
    fetchUserProfile();
  }, [userId, isViewingOtherUser, navigate, setToast]);

  useEffect(() => {
    if (!isAuthenticated || isViewingOtherUser) {
      setMyMintSpaces([]);
      return;
    }
    let cancelled = false;
    getAllMyMintSpaces()
      .then((res) => {
        if (!cancelled && res.success) setMyMintSpaces(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setMyMintSpaces([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isViewingOtherUser, user?.id, user?._id]);

  // Followers and Following, and how the viewer and this person follow each other.
  const loadFollowStats = useCallback(() => {
    if (!profileUserId) return undefined;
    let cancelled = false;
    getFollowStats(profileUserId)
      .then((res) => !cancelled && res.success && setFollowStats(res.data))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [profileUserId]);

  useEffect(() => {
    setFollowStats(null);
    setFollowList(null);
    return loadFollowStats();
  }, [loadFollowStats]);

  // Fetch balance only for own profile (never for other users)
  useEffect(() => {
    if (isViewingOtherUser) {
      setBalance(null);
      return;
    }
    const profileUserId = user?.id ?? user?._id;
    if (profileUserId) {
      getBalance(profileUserId)
        .then((data) => {
          const total = (data?.amount || 0) + (data?.holdBalance || 0);
          setBalance(total);
        })
        .catch(() => {
          setBalance(0);
        });
    }
  }, [userId, isViewingOtherUser, user?.id, user?._id]);

  // Auto-open edit mode if ?edit=true query param exists
  useEffect(() => {
    if (searchParams.get("edit") === "true" && !isViewingOtherUser && isAuthenticated && profilePreview) {
      setProfilePreview(false);
      // Remove query param from URL
      navigate("/profile", { replace: true });
      // Scroll to profile fields after a short delay to ensure DOM is updated
      setTimeout(() => {
        if (profileFieldsRef.current) {
          profileFieldsRef.current.scrollIntoView({ 
            behavior: "smooth", 
            block: "start" 
          });
        }
      }, 300);
    }
  }, [searchParams, isViewingOtherUser, isAuthenticated, profilePreview, setProfilePreview, navigate]);

  const handleSendMessage = async () => {
    if (!userId || !isViewingOtherUser || !messageContent.trim()) return;
    
    try {
      setIsSendingRequest(true);
      const response = await sendMessageRequest(userId, messageContent.trim());
      if (response.success) {
        setShowMessageModal(false);
        setMessageContent("");
        setMessageStatus({
          ...messageStatus,
          hasPendingRequest: true,
          isSender: true,
          conversationId: response.data?.conversationId,
        });
        window.dispatchEvent(new CustomEvent("messages-updated"));
        if (setToast) {
          setToast(t("profile.messageSent") || "Message sent!");
        }
        const cid = response.data?.conversationId;
        if (cid) {
          navigate(`/messages/${cid}`);
        } else {
          navigate("/messages?requests=1");
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      if (setToast) {
        setToast(error.response?.data?.message || t("profile.failedToSendMessage") || "Failed to send message");
      }
    } finally {
      setIsSendingRequest(false);
    }
  };

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const cardBase = "border-separator-default bg-bg-raised";
  const inputBase = "border-separator-default bg-bg-raised text-label-primary focus:border-accent-default focus:";

  // Use viewing user's data if viewing another user, otherwise use current user's data
  const displayUser = isViewingOtherUser ? viewingUser : user;
  const displayAvatar = isViewingOtherUser
    ? getProfileAvatarUrl(viewingUser)
    : avatarImage;
  const displayBanner = isViewingOtherUser
    ? (viewingUser?.profile?.bannerImage)
    : bannerImage;

  const profileSpaces = isViewingOtherUser ? userMintSpaces : myMintSpaces;
  const createdSpaces = profileSpaces.filter((space) => space.ownedByProfile);
  const joinedSpaces = profileSpaces.filter((space) => !space.ownedByProfile);
  const showingCreated = activeSection === "createdMintSpaces";
  const shownSpaces = showingCreated ? createdSpaces : joinedSpaces;
  // The privacy setting covers joined spaces only: created ones always show.
  const joinedHidden = isViewingOtherUser && joinedMintSpacesPrivateOther;
  const spacesCount = (count) => t(count === 1 ? "profile.spacesCountOne" : "profile.joinedMintSpacesCount", { count });

  const renderMintSpaceRow = (space) => {
    const hasQuestions = space.questions && space.questions.length > 0;
    const { isOwner, isMember } = space;
    return (
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => openSpace(space)}
          className={`flex items-center gap-2 rounded-radius-sm border-2 border-accent-default bg-accent-tinted px-4 py-2 text-subheadline-semibold text-accent-text transition hover:bg-mint/30`}
        >
          {space.logo && <img src={space.logo} alt={space.name} className="h-5 w-5 rounded object-cover" />}
          <span>{space.name}</span>
        </button>
        {!isOwner && !isMember && (
          <button
            type="button"
            onClick={() => openSpace(space)}
            className={`px-3 py-1.5 rounded-radius-xs text-footnote-semibold transition ${
              hasQuestions
                ? "bg-accent-tinted text-accent-text hover:bg-mint/30"
                : "bg-accent-default text-accent-on"
            }`}
          >
            {hasQuestions ? t("profile.requestToJoin") : t("profile.join")}
          </button>
        )}
        {isMember && !isOwner && (
          <span className={`px-3 py-1.5 rounded-radius-xs text-footnote-semibold ${mutedText}`}>
            {t("profile.joined")}
          </span>
        )}
      </div>
    );
  };

  if (loadingProfile) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className={mutedText}>{t("profile.loadingProfile")}</p>
      </div>
    );
  }

  /** Figma's cards under the hero: icon and chevron, title, one line. */
  const actionCard = ({ icon, title, line, onClick, key }) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      className="flex flex-col gap-space-8 rounded-radius-lg bg-bg-raised p-space-16 text-left outline-none focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)]"
    >
      <span className="flex items-center justify-between gap-space-8 text-label-secondary">
        <Icon name={icon} size={22} />
        <Icon name="chevron-right" size={18} />
      </span>
      <span className="text-headline text-label-primary">{title}</span>
      <span className="text-footnote text-label-secondary">{line}</span>
    </button>
  );

  return (
    <Page>
      {!isViewingOtherUser && (
        <PageHeader
          eyebrow={t("profile.profile")}
          title={t("profile.customizeCreatorProfile")}
          subtitle={t("profile.updateBannerProfile")}
          action={
            <div className="flex flex-wrap items-center justify-end gap-space-8">
              {!isProfileEditMode && balance !== null && <Badge tone="success">{balance.toFixed(2)} ₾</Badge>}
              {isAuthenticated && (
                <Button size="m" variant={profilePreview ? "filled" : "gray"} onClick={() => setProfilePreview(!profilePreview)}>
                  {profilePreview ? t("profile.editProfile") : t("profile.preview")}
                </Button>
              )}
            </div>
          }
        />
      )}

      <div className="flex flex-col gap-space-24" style={{ overflow: "visible" }}>
        {isViewingOtherUser ? (
          <ProfileHero
            person={viewingUser}
            avatar={displayAvatar}
            banner={displayBanner}
            socialUrl={buildSocialUrl}
            followStats={followStats}
            onOpenFollowList={setFollowList}
          >
            {followStats && (
              <FollowButton
                userId={userId}
                size="m"
                isFollowing={followStats.isFollowing}
                followsYou={followStats.followsYou}
                onChange={(next) => setFollowStats((prev) => ({ ...prev, ...next }))}
                isDark={isDark}
                setToast={setToast}
              />
            )}
            {messageStatus?.hasConversation ||
            (messageStatus?.hasPendingRequest && messageStatus.isSender && messageStatus.conversationId) ? (
              <Button
                variant="gray"
                size="m"
                icon={<Icon name="message" size={20} />}
                onClick={() => navigate(`/messages/${messageStatus.conversationId}`)}
              >
                {t("messages.message")}
              </Button>
            ) : messageStatus?.canSendRequest && !messageStatus.hasPendingRequest ? (
              <Button variant="gray" size="m" icon={<Icon name="message" size={20} />} onClick={() => setShowMessageModal(true)}>
                {t("messages.message")}
              </Button>
            ) : messageStatus?.hasPendingRequest && messageStatus.isSender ? (
              <Button variant="gray" size="m" disabled>
                {t("mintSpace.requestSent")}
              </Button>
            ) : null}
          </ProfileHero>
        ) : (
        /* Banner + avatar */
        <div
          className={`rounded-radius-lg bg-bg-raised ${isProfileEditMode ? "min-[1100px]:bg-bg-raised-end" : ""}`}
          style={{ overflow: 'visible' }}
        >
          {/* Banner */}
          <div
            className={`relative rounded-t-radius-lg ${isProfileEditMode ? "bg-accent-tinted" : "bg-fill-quaternary"}`}
            style={{ overflow: 'visible' }}
          >
            <div 
              className={`${
                isProfileEditMode ? "h-[140px] cursor-pointer" : "h-[160px] min-[700px]:h-[240px]"
              } w-full relative overflow-hidden rounded-t-radius-lg banner-container`}
              onMouseEnter={(e) => {
                e.stopPropagation();
                const overlay = e.currentTarget.querySelector('.banner-overlay');
                if (overlay) overlay.classList.add('show');
              }}
              onMouseLeave={(e) => {
                const overlay = e.currentTarget.querySelector('.banner-overlay');
                if (overlay) overlay.classList.remove('show');
              }}
            >
              {displayBanner ? (
                <img
                  src={displayBanner}
                  alt="Profile banner"
                  className="h-full w-full object-cover"
                />
              ) : null}
              {isUploadingBanner && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-30">
                  <div className="flex flex-col items-center gap-2">
                    <FiLoader className="animate-spin text-white text-2xl" />
                    <p className="text-white text-subheadline">Uploading banner...</p>
                  </div>
                </div>
              )}
              {isAuthenticated && !profilePreview && (
                <>
                  <input
                    ref={bannerInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleBannerChange}
                    disabled={isUploadingBanner}
                  />
                  <div
                    onClick={(e) => {
                      if (isUploadingBanner) return;
                      e.stopPropagation();
                      bannerInputRef.current?.click();
                    }}
                    className={`banner-overlay absolute inset-0 bg-white/0 transition-all duration-200 flex items-center justify-center ${isUploadingBanner ? 'pointer-events-none' : ''}`}
                  >
                  </div>
                </>
              )}
            </div>

            {/* Avatar overlapping banner */}
            {!isProfileEditMode && (
              <div
                className="absolute left-6 bottom-0 avatar-container"
                style={{ transform: 'translateY(50%)', zIndex: 20 }}
              >
                <div
                  className={`relative h-32 w-32 rounded-full border-4 ${
                    isDark ? "border-slate-900/80 bg-slate-900/80" : "border-white bg-white"
                  } shadow-xl overflow-hidden`}
                >
                  {displayAvatar ? (
                    <img
                      src={displayAvatar}
                      alt="Profile avatar"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className={`h-full w-full flex items-center justify-center ${
                      isDark ? "bg-slate-700" : "bg-slate-200"
                    }`}>
                      <svg
                        className={`h-16 w-16 text-label-secondary`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {isProfileEditMode ? (
            /* Figma B15.2 "My profile · Edit" */
            <div className="flex flex-col gap-space-12 px-space-24 pb-space-24 pt-space-16">
              <div className="flex items-end gap-space-16">
                <button
                  type="button"
                  aria-label={t("profile.changeAvatar")}
                  title={t("profile.changeAvatar")}
                  disabled={isUploadingAvatar}
                  onClick={() => avatarInputRef.current?.click()}
                  className="group relative shrink-0 rounded-14 outline-none focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)]"
                >
                  <Avatar size="l" src={displayAvatar} name={displayUser?.username || displayUser?.businessName} />
                  <span
                    className={`absolute inset-0 flex items-center justify-center rounded-14 bg-black/50 text-white transition-opacity ${
                      isUploadingAvatar ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                    }`}
                  >
                    {isUploadingAvatar ? <FiLoader className="animate-spin" /> : <Icon name="image" size={18} />}
                  </span>
                </button>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange}
                  disabled={isUploadingAvatar}
                />
                <div className="flex min-w-0 flex-1 flex-col gap-space-4">
                  <h3 className="truncate text-title-2 text-label-primary">
                    {displayUser?.username || displayUser?.businessName || t("profile.yourHandle")}
                  </h3>
                  {followStats && (
                    <p className="flex flex-wrap items-center gap-x-[6px] text-footnote text-label-secondary">
                      <button type="button" onClick={() => setFollowList("followers")} className="hover:underline">
                        {followStats.followers} {t(followStats.followers === 1 ? "follows.followerCountOne" : "follows.followerCount")}
                      </button>
                      <span aria-hidden="true">·</span>
                      <button type="button" onClick={() => setFollowList("following")} className="hover:underline">
                        {followStats.following} {t("follows.followingCount")}
                      </button>
                    </p>
                  )}
                </div>
              </div>

              <h4 className="text-headline text-label-primary">{t("profile.socialLinks")}</h4>
              <div className="flex flex-wrap items-center gap-space-8">
                {SOCIAL_BADGES.map(([id, label]) => {
                  const connected = id === "instagram" ? instagramVerified : Boolean(socials[id]);
                  return (
                    <button
                      key={id}
                      type="button"
                      aria-pressed={connected}
                      onClick={() => {
                        if (id === "instagram") {
                          setShowInstagramVerificationModal(true);
                          return;
                        }
                        setSocialModal(id);
                        setTempSocialUrl(socials[id]);
                      }}
                      className={`inline-flex h-6 items-center gap-space-4 rounded-radius-sm px-space-8 text-caps-button-s uppercase outline-none transition-opacity hover:opacity-80 focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)] ${
                        connected ? "bg-accent-tinted text-accent-tinted-label" : "bg-fill-secondary text-label-primary"
                      }`}
                    >
                      {label}
                      {connected && <Icon name="check" size={12} />}
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-col gap-[6px]">
                <FieldLabel htmlFor="profile-bio">{t("profile.bio")}</FieldLabel>
                <textarea
                  id="profile-bio"
                  placeholder={t("profile.bioPlaceholder")}
                  value={profileData.bio}
                  onChange={(e) => setProfileData((prev) => ({ ...prev, bio: e.target.value }))}
                  className="h-[120px] w-full resize-none rounded-radius-md bg-fill-tertiary px-space-16 py-space-12 text-callout text-label-primary shadow-edge outline-none placeholder:text-label-tertiary focus:shadow-[inset_0_0_0_2px_var(--color-accent-text)]"
                />
              </div>

              <div ref={profileFieldsRef} className="flex flex-col gap-space-12">
                <TextField
                  label={t("profile.username")}
                  autoComplete="username"
                  placeholder={t("profile.usernamePlaceholder")}
                  value={profileData.username || ""}
                  onChange={(e) =>
                    setProfileData((prev) => ({
                      ...prev,
                      username: e.target.value.replace(/\s/g, ""),
                    }))
                  }
                />
                {user?.accountType === "creator" && (
                  <div className={FIELD_PAIRS}>
                    <TextField
                      label={t("profile.firstName")}
                      placeholder={t("profile.firstNamePlaceholder")}
                      value={profileData.firstName || ""}
                      onChange={(e) => setProfileData((prev) => ({ ...prev, firstName: e.target.value }))}
                    />
                    <TextField
                      label={t("profile.lastName")}
                      placeholder={t("profile.lastNamePlaceholder")}
                      value={profileData.lastName || ""}
                      onChange={(e) => setProfileData((prev) => ({ ...prev, lastName: e.target.value }))}
                    />
                    <TextField
                      label={t("profile.dateOfBirth")}
                      type="date"
                      style={{ colorScheme: isDark ? "dark" : "light" }}
                      value={profileData.dateOfBirth || ""}
                      onChange={(e) => setProfileData((prev) => ({ ...prev, dateOfBirth: e.target.value }))}
                    />
                    <TextField
                      label={t("profile.location")}
                      placeholder={t("profile.locationPlaceholder")}
                      value={profileData.location || ""}
                      onChange={(e) => setProfileData((prev) => ({ ...prev, location: e.target.value }))}
                    />
                  </div>
                )}
                {user?.accountType === "business" && (
                  <div className={FIELD_PAIRS}>
                    <div className="flex flex-col gap-[6px]">
                      <FieldLabel htmlFor="profile-business-type">{t("profile.businessType")}</FieldLabel>
                      {/* A pop-up in the field's own size and fill, so it sits in line with the text fields. */}
                      <PopUpSelect
                        id="profile-business-type"
                        label={t("profile.businessType")}
                        value={profileData.businessType || ""}
                        onChange={(businessType) => setProfileData((prev) => ({ ...prev, businessType }))}
                        placeholder={t("profile.selectBusinessType")}
                        className="w-full !h-[48px] !rounded-radius-md !bg-fill-tertiary !pl-space-16 !pr-space-16 min-[600px]:!h-[44px]"
                      >
                        <option value="LLC (Limited Liability Company)">LLC (Limited Liability Company)</option>
                        <option value="Individual / Sole Proprietor">Individual / Sole Proprietor</option>
                        <option value="Partnership">Partnership</option>
                        <option value="Corporation / JSC">Corporation / JSC</option>
                        <option value="Other">Other</option>
                      </PopUpSelect>
                    </div>
                    {profileData.businessType === "Other" && (
                      <TextField
                        label={t("profile.enterBusinessType")}
                        placeholder={t("profile.enterBusinessTypePlaceholder")}
                        value={profileData.businessTypeOther || ""}
                        onChange={(e) => setProfileData((prev) => ({ ...prev, businessTypeOther: e.target.value }))}
                      />
                    )}
                    <TextField
                      label={t("profile.companyId")}
                      placeholder={t("profile.companyIdPlaceholder")}
                      value={profileData.companyIdentificationNumber || ""}
                      onChange={(e) => setProfileData((prev) => ({ ...prev, companyIdentificationNumber: e.target.value }))}
                    />
                    <TextField
                      label={t("profile.location")}
                      placeholder={t("profile.locationPlaceholder")}
                      value={profileData.location || ""}
                      onChange={(e) => setProfileData((prev) => ({ ...prev, location: e.target.value }))}
                    />
                  </div>
                )}
                <TextField
                  label={t("profile.website")}
                  type="url"
                  placeholder="https://yourwebsite.com"
                  value={profileData.website || ""}
                  onChange={(e) => setProfileData((prev) => ({ ...prev, website: e.target.value }))}
                />
              </div>

              <div className="flex items-center justify-between gap-space-12 rounded-radius-md border border-separator-default p-space-16">
                <div className="flex min-w-0 flex-1 flex-col gap-space-4">
                  <p className="text-subheadline-semibold text-label-primary">{t("profile.joinedMintSpacesPrivateLabel")}</p>
                  <p className="text-footnote text-label-secondary">{t("profile.joinedMintSpacesPrivateHint")}</p>
                </div>
                <Switch
                  on={!!profileData.joinedMintSpacesPrivate}
                  label={t("profile.joinedMintSpacesPrivateLabel")}
                  onClick={() =>
                    setProfileData((prev) => ({
                      ...prev,
                      joinedMintSpacesPrivate: !prev.joinedMintSpacesPrivate,
                    }))
                  }
                />
              </div>
              <DeleteAccountSection isDark={isDark} setToast={setToast} />
            </div>
          ) : (
          <div className="px-space-24 pb-space-16 pt-20">
            <div className="space-y-4">
              <div>
                <h3 className="text-title-2 text-label-primary">
                  {displayUser?.username || displayUser?.businessName || (isAuthenticated ? t("profile.yourHandle") : t("common.user"))}
                </h3>
                {displayUser?.createdAt && (
                  <p className="mt-space-4 text-footnote text-label-secondary">
                    Joined {new Date(displayUser.createdAt).toLocaleDateString('en-US', { month: 'short' })} {new Date(displayUser.createdAt).getFullYear()}
                  </p>
                )}
                {followStats && (
                  <div className="mt-space-8 flex flex-wrap items-center gap-x-space-16 gap-y-space-8 text-footnote">
                    {["followers", "following"].map((key) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFollowList(key)}
                        className={`${mutedText} transition hover:underline`}
                      >
                        <span className={`font-bold tabular-nums ${baseText}`}>{followStats[key]}</span>{" "}
                        {key === "following"
                          ? t("follows.followingCount")
                          : followStats.followers === 1
                          ? t("follows.followerCountOne")
                          : t("follows.followerCount")}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Social links */}
              {isAuthenticated && (
                  <div>
                    <p className={`text-subheadline-semibold mb-3 ${baseText}`}>Social Links</p>
                    {(socials.youtube || socials.instagram || socials.facebook || socials.tiktok) ? (
                      <div className="flex flex-wrap gap-3">
                        {socials.youtube && (
                          <a
                            href={buildSocialUrl("youtube", socials.youtube)}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 rounded-radius-sm border-2 border-accent-default bg-accent-tinted px-4 py-2 text-subheadline-semibold text-accent-text transition hover:bg-mint/30"
                          >
                            <FaYoutube className="h-4 w-4" />
                            <span>YouTube</span>
                          </a>
                        )}
                        {socials.instagram && (
                                <a
                                  href={buildSocialUrl("instagram", socials.instagram)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex items-center gap-2 rounded-radius-sm border-2 border-accent-default bg-accent-tinted px-4 py-2 text-subheadline-semibold text-accent-text transition hover:bg-mint/30"
                                >
                                  <FaInstagram className="h-4 w-4" />
                                  <span>Instagram</span>
                                </a>
                              )}
                              {socials.facebook && (
                                <a
                                  href={buildSocialUrl("facebook", socials.facebook)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex items-center gap-2 rounded-radius-sm border-2 border-accent-default bg-accent-tinted px-4 py-2 text-subheadline-semibold text-accent-text transition hover:bg-mint/30"
                                >
                                  <FaFacebookF className="h-4 w-4" />
                                  <span>Facebook</span>
                                </a>
                              )}
                              {socials.tiktok && (
                                <a
                                  href={buildSocialUrl("tiktok", socials.tiktok)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex items-center gap-2 rounded-radius-sm border-2 border-accent-default bg-accent-tinted px-4 py-2 text-subheadline-semibold text-accent-text transition hover:bg-mint/30"
                                >
                                  <FaTiktok className="h-4 w-4" />
                                  <span>TikTok</span>
                                </a>
                        )}
                      </div>
                    ) : (
                      <p className={`text-subheadline ${mutedText}`}>No social links added yet.</p>
                    )}
                  </div>
              )}

              {/* Bio */}
              {isAuthenticated ? (
                  <div>
                    {profileData.bio ? (
                      <p className={`text-subheadline leading-relaxed ${baseText}`}>{profileData.bio}</p>
                    ) : (
                      <p className={`text-subheadline italic ${mutedText}`}>{t("profile.noBioYet")}</p>
                    )}
                  </div>
              ) : (
                <p className={`text-subheadline ${mutedText}`}>
                  Please sign in to customize your profile.
                </p>
              )}

              {/* Location and Website */}
              {isAuthenticated && (
                  <div className="space-y-2">
                    {profileData.location && (
                      <div className="flex items-center gap-2">
                        <span className={`text-subheadline ${mutedText}`}>📍</span>
                        <span className={`text-subheadline ${baseText}`}>{profileData.location}</span>
                      </div>
                    )}
                    {profileData.website && (
                      <div className="flex items-center gap-2">
                        <span className={`text-subheadline ${mutedText}`}>🌐</span>
                        <a
                          href={profileData.website}
                          target="_blank"
                          rel="noreferrer"
                          className={`text-subheadline text-accent-text hover:underline ${baseText}`}
                        >
                          {profileData.website}
                        </a>
                      </div>
                    )}
                  </div>
              )}
            </div>
          </div>
          )}
        </div>
        )}

        {/* Joined and created spaces */}
        {isAuthenticated && profilePreview && activeSection === null && (
          <div className="grid grid-cols-1 gap-space-16 md:grid-cols-2 lg:gap-space-24">
            {actionCard({
              key: "joined",
              icon: "users",
              title: t("profile.joinedSpaces"),
              line: joinedHidden ? t("profile.joinedMintSpacesPrivateMessage") : spacesCount(joinedSpaces.length),
              onClick: () => setActiveSection("joinedMintSpaces"),
            })}
            {actionCard({
              key: "created",
              icon: "plus",
              title: t("profile.createdSpaces"),
              line: spacesCount(createdSpaces.length),
              onClick: () => setActiveSection("createdMintSpaces"),
            })}
          </div>
        )}

        {/* Joined or created spaces */}
        {isAuthenticated && profilePreview && activeSection !== null && (
          <div className="space-y-6 animate-slideInRight">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className={`text-title-1 ${baseText}`}>
                  {t(showingCreated ? "profile.createdSpaces" : "profile.joinedSpaces")}
                </h3>
                <p className={`text-subheadline ${mutedText} mt-1`}>
                  {joinedHidden && !showingCreated
                    ? t("profile.joinedMintSpacesPrivateMessage")
                    : spacesCount(shownSpaces.length)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActiveSection(null)}
                className={`rounded-radius-sm border px-4 py-2 text-subheadline-semibold transition shrink-0 ${cardBase}`}
              >
                {t("common.back")}
              </button>
            </div>

            {joinedHidden && !showingCreated ? (
              <div
                className={`flex min-h-[240px] flex-col items-center justify-center rounded-radius-md border px-6 py-16 text-center ${cardBase}`}
              >
                <FiUsers className={`mb-4 h-12 w-12 ${mutedText} opacity-60`} />
                <p className={`max-w-md text-base font-medium ${baseText}`}>
                  {t("profile.joinedMintSpacesPrivateMessage")}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {shownSpaces.length === 0 ? (
                  <div
                    className={`flex min-h-[180px] flex-col items-center justify-center rounded-radius-md border px-6 py-12 text-center ${cardBase}`}
                  >
                    <p className={`${mutedText}`}>
                      {isViewingOtherUser
                        ? t("profile.joinedMintSpacesEmptyOther")
                        : t(showingCreated ? "profile.createdSpacesEmpty" : "profile.joinedMintSpacesEmpty")}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {shownSpaces.map((space) => (
                      <div
                        key={space._id}
                        className={`rounded-radius-md border p-5 transition ${cardBase}`}
                      >
                        {renderMintSpaceRow(space)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Instagram Verification Modal */}
        {!isViewingOtherUser && (
          <InstagramVerificationModal
            isOpen={showInstagramVerificationModal}
            setIsOpen={setShowInstagramVerificationModal}
            setToast={setToast}
            isDark={isDark}
            onVerificationUpdate={(verified) => {
              setInstagramVerified(verified);
            }}
          />
        )}

        {/* Message Modal */}
        {showMessageModal && isViewingOtherUser && (
          <>
            <div
              className="fixed inset-0 bg-overlay-scrim z-50"
              onClick={() => {
                setShowMessageModal(false);
                setMessageContent("");
              }}
            />
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className={`${cardBase} border-2 rounded-radius-lg p-6 max-w-md w-full`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-title-3 ${baseText}`}>
                    {t("messages.message") || "Send Message"} {displayUser?.username}
                  </h3>
                  <button
                    onClick={() => {
                      setShowMessageModal(false);
                      setMessageContent("");
                    }}
                    className={`p-2 rounded-radius-xs hover:bg-fill-quaternary`}
                  >
                    <FiX className={`h-5 w-5 ${mutedText}`} />
                  </button>
                </div>
                <textarea
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  placeholder={t("profile.messageRequestPlaceholder") || "What would you like to chat about? Your message will be sent with the request so they know what you want to discuss."}
                  className={`w-full ${inputBase} rounded-radius-sm p-4 min-h-[120px] resize-none`}
                  autoFocus
                />
                <div className="flex items-center gap-3 mt-4">
                  <button
                    onClick={() => {
                      setShowMessageModal(false);
                      setMessageContent("");
                    }}
                    className={`flex-1 px-4 py-2 rounded-radius-sm border font-semibold transition ${
                      "border-separator-default bg-bg-raised hover:bg-fill-quaternary text-label-secondary"
                    }`}
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    onClick={handleSendMessage}
                    disabled={isSendingRequest || !messageContent.trim()}
                    className="flex-1 px-4 py-2 rounded-radius-sm bg-accent-default text-accent-on font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSendingRequest ? t("profile.sending") || "Sending..." : t("messages.send") || "Send"}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        <FollowListModal
          userId={profileUserId}
          list={followList}
          onListChange={setFollowList}
          onClose={() => setFollowList(null)}
          onFollowsChanged={loadFollowStats}
          counts={followStats}
          isDark={isDark}
          setToast={setToast}
        />
      </div>
    </Page>
  );
};

export default ProfilePage;

